import { FC, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import Routes, { DELIVERY_ID_PARAM } from 'routes';
import { TrayBarcodeAssignment } from 'api/models/Users';
import { useTrayBarcodeAssignmentSetting } from 'api/hooks/useTrayBarcodeAssignmentSetting';
import { useCaptureTrayNumberForLocation } from 'api/hooks/useLocationSetting';
import BarcodeCaptureDialog from 'components/BarcodeCaptureDialog';
import { TrayNumberDialog } from 'components/CheckInOut/components/TrayNumberDialog';
import { DeliveryCheckedInSetsDialog } from 'components/DeliveryCheckedInSetsDialog/DeliveryCheckedInSetsDialog';
import { SetsCheckedInDialog } from 'components/SetsCheckedInDialog/SetsCheckedInDialog';

export type VisibleDialog =
  | 'ConfirmDecantTray'
  | 'DeliveryCheckedIn'
  | 'TrayNumber'
  | 'BarcodeCapture'
  | 'SetsCheckedIn'
  | 'ConfirmAnotherDecantedTray';

type Props = {
  deliveryId: string;
  orderRecordId?: string;
  noSets: boolean;
  visibleDialog: VisibleDialog | null;
  setVisibleDialog: (dialog: VisibleDialog | null) => void;
  onConfirm: (trayNumber: number, assetNumber?: string) => void;
};

export const CheckInTrayDialogs: FC<Props> = ({
  deliveryId,
  orderRecordId,
  noSets,
  visibleDialog,
  setVisibleDialog,
  onConfirm,
}) => {
  const navigate = useNavigate();
  const { trayBarcodeAssignmentSetting } = useTrayBarcodeAssignmentSetting();
  const { forLocation: numberTraysEnabled } = useCaptureTrayNumberForLocation();

  const trayNumberRef = useRef<number | null>(null);

  const onTrayNumberSubmit = useCallback(
    (trayNum: number) => {
      if (trayBarcodeAssignmentSetting == TrayBarcodeAssignment.Capture) {
        trayNumberRef.current = trayNum;
        setVisibleDialog('BarcodeCapture');
        return;
      }
      onConfirm(trayNum);
    },
    [onConfirm, setVisibleDialog, trayBarcodeAssignmentSetting],
  );

  const onBarcodeDetected = useCallback(
    (code?: string) => {
      if (trayNumberRef.current !== null) {
        onConfirm(trayNumberRef.current, code);
      }
      setVisibleDialog(null);
    },
    [onConfirm, setVisibleDialog],
  );

  return (
    <>
      {'SetsCheckedIn' == visibleDialog && (
        <SetsCheckedInDialog
          deliveryId={deliveryId}
          onClose={() => setVisibleDialog(null)}
          onReturnToDeliveryInformationPage={() =>
            navigate(Routes.CheckInWorkflow.path.replace(DELIVERY_ID_PARAM, deliveryId))
          }
          onReturnToActionItems={() => navigate(Routes.ActionItems.path)}
          noSets={noSets}
        />
      )}

      {'DeliveryCheckedIn' == visibleDialog && (
        <DeliveryCheckedInSetsDialog
          deliveryId={deliveryId}
          orderRecordId={orderRecordId || ''}
          onClose={() => setVisibleDialog(null)}
        />
      )}

      {'TrayNumber' == visibleDialog && (
        <TrayNumberDialog
          orderRecordId={orderRecordId}
          autoConfirm={!numberTraysEnabled}
          onClose={() => setVisibleDialog(null)}
          onConfirmClick={onTrayNumberSubmit}
        />
      )}

      {'BarcodeCapture' == visibleDialog && (
        <BarcodeCaptureDialog
          onDetected={onBarcodeDetected}
          onSkip={() => onBarcodeDetected()}
          onClose={() => setVisibleDialog(null)}
          onBack={() => {
            setVisibleDialog('TrayNumber');
          }}
        />
      )}
    </>
  );
};
